import React, { useState } from 'react';
import { ShieldCheck, Loader2, Check } from 'lucide-react';

// Per-workspace "allowed embed origins" editor. Uses the pasted management
// key (same as Try-It). An empty list = embeddable from anywhere.
export default function EmbedOriginsManager({ apiBase, bearerKey }) {
  const [workspaceId, setWorkspaceId] = useState('');
  const [origins, setOrigins] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const canUse = !!bearerKey?.trim();

  const authHeaders = () => ({ Authorization: `Bearer ${bearerKey.trim()}` });

  const load = async () => {
    if (!canUse || !workspaceId.trim()) return;
    setLoading(true); setMsg(null);
    try {
      const res = await fetch(`${apiBase}/api/mgmt/v1/workspaces/${encodeURIComponent(workspaceId.trim())}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setOrigins((data.allowed_origins || []).join('\n'));
      setMsg({ ok: true, text: `Loaded ${(data.allowed_origins || []).length} origin(s).` });
    } catch (e) {
      setMsg({ ok: false, text: String(e.message || e) });
    } finally { setLoading(false); }
  };

  const save = async () => {
    if (!canUse || !workspaceId.trim()) return;
    const list = origins.split('\n').map(s => s.trim()).filter(Boolean);
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${apiBase}/api/mgmt/v1/workspaces/${encodeURIComponent(workspaceId.trim())}/embed-origins`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ origins: list }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setOrigins((data.allowed_origins || []).join('\n'));
      setMsg({ ok: true, text: `Saved ${(data.allowed_origins || []).length} allowed origin(s).` });
    } catch (e) {
      setMsg({ ok: false, text: String(e.message || e) });
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-border/40 bg-muted/10 px-4 py-3 mb-6" data-testid="embed-origins-manager">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
        <span className="text-xs font-medium">Allowed embed origins</span>
        <span className="text-[10px] text-muted-foreground">— lock which sites may mount a workspace&rsquo;s embeds. Empty = any site.</span>
      </div>
      <div className="flex gap-2 mb-2">
        <input
          value={workspaceId}
          onChange={(e) => setWorkspaceId(e.target.value)}
          placeholder="workspace_id (e.g. ws_…)"
          spellCheck={false}
          className="flex-1 font-mono text-xs px-3 py-1.5 rounded border border-border/40 bg-background/60"
          data-testid="embed-origins-workspace"
        />
        <button
          onClick={load}
          disabled={!canUse || !workspaceId.trim() || loading}
          className="text-xs px-3 py-1.5 rounded border border-border/40 bg-muted/30 disabled:opacity-40"
          data-testid="embed-origins-load"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Load'}
        </button>
      </div>
      <textarea
        value={origins}
        onChange={(e) => setOrigins(e.target.value)}
        rows={3}
        spellCheck={false}
        placeholder={'https://app.acme.com\nhttps://analytics.acme.com'}
        className="w-full font-mono text-[11px] px-3 py-1.5 rounded border border-border/40 bg-background/60 mb-2"
        data-testid="embed-origins-textarea"
      />
      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={!canUse || !workspaceId.trim() || saving}
          className="text-xs px-3 py-1.5 rounded font-medium bg-primary text-primary-foreground disabled:opacity-40 disabled:bg-muted disabled:text-muted-foreground inline-flex items-center gap-1.5"
          data-testid="embed-origins-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save origins
        </button>
        {!canUse && <span className="text-[10px] text-amber-300">paste an API key above to enable</span>}
        {msg && <span className={`text-[11px] ${msg.ok ? 'text-emerald-400' : 'text-rose-400'}`} data-testid="embed-origins-msg">{msg.text}</span>}
      </div>
      <p className="text-[10px] text-muted-foreground mt-2">One origin per line as <code>scheme://host[:port]</code> (no path). The list is baked into each minted session token and enforced by the embed SDK.</p>
    </div>
  );
}
